import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { BookUser, ChevronLeft, Star } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { SignSheet } from "@/components/wallet/SignSheet";
import { useWallet } from "@/lib/wallet/store";
import { overviewQuery, priceQuery } from "@/lib/wallet/queries";
import { quoteSendFee } from "@/lib/wallet/fees";
import { addressFingerprint, formatAmount, truncateAddress } from "@/lib/wallet/format";
import { decodeXAddress, isValidAddress, xrpToDrops } from "@/lib/wallet/xrpl";

export const Route = createFileRoute("/send")({
  staticData: { sitemap: false },
  validateSearch: (search: Record<string, unknown>): { to?: string; tag?: string } => ({
    ...(typeof search["to"] === "string" ? { to: search["to"] } : {}),
    ...(typeof search["tag"] === "string" ? { tag: search["tag"] } : {}),
  }),
  head: () => ({
    meta: [
      { title: "Send — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Send XRP or issued tokens from your Cobalt Wallet account with an explicit, hold-to-sign confirmation.",
      },
      { property: "og:title", content: "Send — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Review destination, amount, tag and fee before signing locally.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <SendScreen />
    </Gate>
  ),
});

function SendScreen() {
  const { address, submit, settings, contacts, saveContact } = useWallet();
  const search = Route.useSearch();
  const overview = useQuery(overviewQuery(address));
  const price = useQuery(priceQuery(true));
  const tokens = overview.data?.tokens ?? [];

  const [assetKey, setAssetKey] = useState("XRP");
  const [destination, setDestination] = useState(search.to ?? "");
  const [tag, setTag] = useState(search.tag ?? "");
  const [amount, setAmount] = useState("");
  const [checked, setChecked] = useState<null | { valid: boolean; classic: string; tag: number | null }>(
    null,
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");

  const known = useMemo(
    () => contacts.find((c) => c.address === (checked?.classic ?? destination.trim())) ?? null,
    [contacts, checked, destination],
  );


  const asset = useMemo(() => {
    if (assetKey === "XRP") return null;
    return tokens.find((t) => `${t.currency}:${t.issuer}` === assetKey) ?? null;
  }, [assetKey, tokens]);

  const available = asset ? Number(asset.balance) : (overview.data?.spendable ?? 0);
  const numericAmount = Number(amount);
  const overBalance = numericAmount > available;
  const largeAmount = !asset && numericAmount >= settings.largeAmountThreshold;

  const xrpUsd = price.data ?? null;
  // Service fee is tiered on USD value and only priced for XRP payments.
  const usdValue = !asset && xrpUsd && numericAmount > 0 ? numericAmount * xrpUsd : null;
  const serviceFee = useMemo(
    () => quoteSendFee({ usdValue, xrpUsd }),
    [usdValue, xrpUsd],
  );

  const verify = async () => {
    const value = destination.trim();
    if (!value) return;
    const valid = await isValidAddress(value);
    if (!valid) {
      setChecked({ valid: false, classic: value, tag: null });
      return;
    }
    const decoded = await decodeXAddress(value);
    setChecked({ valid: true, classic: decoded.address, tag: decoded.tag });
    if (decoded.tag !== null) setTag(String(decoded.tag));
  };

  useEffect(() => {
    if (search.to) void verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.to]);

  const canReview =
    checked?.valid && numericAmount > 0 && !overBalance && Boolean(address) && !Number.isNaN(numericAmount);


  const buildTx = (): Record<string, unknown> => {
    const base: Record<string, unknown> = {
      TransactionType: "Payment",
      Account: address,
      Destination: checked!.classic,
      Amount: asset
        ? { currency: asset.currency, issuer: asset.issuer, value: amount }
        : xrpToDrops(amount),
    };
    if (tag.trim()) base["DestinationTag"] = Number(tag.trim());
    return base;
  };

  const send = async () => {
    setBusy(true);
    try {
      const result = await submit(buildTx(), { fee: serviceFee });
      if (result.succeeded) {
        toast.success(`Payment validated · ${result.hash.slice(0, 10)}…`);
        if (result.serviceFee?.charged) {
          toast.message(`Service fee ${result.serviceFee.xrp} XRP collected.`);
        } else if (result.serviceFee && !result.serviceFee.charged) {
          toast.message("Payment sent. Service fee could not be collected.");
        }
        setSheetOpen(false);
        setAmount("");
        setDestination("");
        setTag("");
        setChecked(null);
        void overview.refetch();
      } else {
        toast.error(`Ledger rejected: ${result.result}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Signing failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          aria-label="Back to home"
          className="grid size-8 place-items-center rounded-lg border border-border text-muted"
        >
          <ChevronLeft className="size-4" />
        </Link>
        <h1 className="text-[18px] font-medium tracking-tight">Send</h1>
      </div>

      <section className="panel space-y-3 p-4">
        <div>
          <p className="label-instrument">Asset</p>
          <select
            value={assetKey}
            onChange={(e) => setAssetKey(e.target.value)}
            className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none focus:border-accent"
          >
            <option value="XRP">XRP · {formatAmount(overview.data?.spendable ?? 0, true)}</option>
            {tokens.map((t) => (
              <option key={`${t.currency}:${t.issuer}`} value={`${t.currency}:${t.issuer}`}>
                {t.code} · {formatAmount(t.balance, true)} · {truncateAddress(t.issuer)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <p className="label-instrument">Destination address</p>
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className="readout flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-accent"
            >
              <BookUser className="size-3" /> {contacts.length ? "Address book" : "Saved: none"}
            </button>
          </div>

          {pickerOpen && contacts.length > 0 ? (
            <ul className="animate-tick mt-1.5 max-h-40 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-background">
              {contacts.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setDestination(c.address);
                      setTag(c.tag ?? "");
                      setChecked({ valid: true, classic: c.address, tag: null });
                      setPickerOpen(false);
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[12px]">{c.label}</span>
                      <span className="readout block text-[10px] text-faint">
                        {truncateAddress(c.address, 5, 5)}
                        {c.tag ? ` · tag ${c.tag}` : ""}
                      </span>
                    </span>
                    <span className="readout shrink-0 text-[10px] text-accent">
                      {addressFingerprint(c.address)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {pickerOpen && contacts.length === 0 ? (
            <p className="readout mt-1.5 rounded-lg border border-border bg-background p-2.5 text-[10px] leading-relaxed text-faint">
              No saved recipients yet.{" "}
              <Link to="/contacts" className="text-accent">
                Open the address book
              </Link>{" "}
              to add one.
            </p>
          ) : null}

          <input
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
              setChecked(null);
            }}
            onBlur={() => void verify()}
            spellCheck={false}
            placeholder="r… or X…"
            className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[12px] outline-none placeholder:text-faint focus:border-accent"
          />
          {checked ? (
            checked.valid ? (
              <p className="readout mt-1.5 text-[10px] text-success">
                Valid · fingerprint {addressFingerprint(checked.classic)} ·{" "}
                {truncateAddress(checked.classic, 6, 6)}
              </p>
            ) : (
              <p className="readout mt-1.5 text-[10px] text-danger">
                Not a valid XRPL address. Nothing will be signed.
              </p>
            )
          ) : (
            <p className="readout mt-1.5 text-[10px] text-faint">
              Compare the fingerprint with your recipient before sending.
            </p>
          )}

          {known ? (
            <p className="readout mt-1.5 flex items-center gap-1 text-[10px] text-accent">
              <Star className="size-3" /> Saved as “{known.label}”
            </p>
          ) : checked?.valid ? (
            <div className="mt-2 flex gap-2">
              <input
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value.slice(0, 40))}
                placeholder="Save as… (label)"
                className="field h-9 flex-1 px-3 text-[11px]"
              />
              <button
                type="button"
                disabled={!saveLabel.trim()}
                onClick={() => {
                  try {
                    saveContact({
                      label: saveLabel,
                      address: checked.classic,
                      tag: tag.trim(),
                    });
                    setSaveLabel("");
                    toast.success("Recipient saved to your address book.");
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "Could not save.");
                  }
                }}
                className="btn-outline-accent h-9 px-3 text-[10px] disabled:opacity-40"
              >
                Save
              </button>
            </div>
          ) : null}
        </div>


        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="label-instrument">Amount</p>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="0.00"
              className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none placeholder:text-faint focus:border-accent"
            />
          </div>
          <div>
            <p className="label-instrument">Destination tag</p>
            <input
              value={tag}
              onChange={(e) => setTag(e.target.value.replace(/[^0-9]/g, ""))}
              inputMode="numeric"
              placeholder="optional"
              className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none placeholder:text-faint focus:border-accent"
            />
          </div>
        </div>

        <div className="readout flex items-center justify-between text-[10px]">
          <span className="text-faint">AVAILABLE</span>
          <button
            type="button"
            onClick={() => setAmount(String(available))}
            className="text-accent"
          >
            {formatAmount(available, true)} {asset ? asset.code : "XRP"} · MAX
          </button>
        </div>

        {overBalance ? (
          <p className="readout rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-[10px] text-danger">
            Amount exceeds spendable balance. The XRPL reserve must stay funded.
          </p>
        ) : null}
      </section>

      <button
        type="button"
        disabled={!canReview}
        onClick={() => setSheetOpen(true)}
        className="readout h-12 w-full rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink disabled:opacity-40"
      >
        Review transaction
      </button>

      <SignSheet
        open={sheetOpen}
        title="Sign transaction"
        checksum={checked?.valid ? "✓" : "!"}
        busy={busy}
        rows={[
          { label: "Operation", value: "Payment" },
          {
            label: "Amount",
            value: `${formatAmount(amount, true)} ${asset ? asset.code : "XRP"}`,
          },
          { label: "Destination", value: checked?.classic ?? "" },
          { label: "Fingerprint", value: addressFingerprint(checked?.classic ?? ""), emphasis: "accent" },
          { label: "Tag", value: tag.trim() || "none" },
          {
            label: "Cobalt service fee",
            value:
              serviceFee.xrp > 0
                ? `${serviceFee.xrp} XRP · ${serviceFee.basis}`
                : "Free",
            emphasis: serviceFee.xrp > 0 ? "accent" : "default",
          },
          { label: "Network", value: "XRPL mainnet" },
        ]}
        warning={
          largeAmount
            ? `This is a large transfer (over ${formatAmount(settings.largeAmountThreshold)} XRP). Confirm the destination fingerprint matches.`
            : undefined
        }
        onCancel={() => setSheetOpen(false)}
        onConfirm={() => void send()}
      />
    </div>
  );
}
